import { PlacementTask } from '../enums/placement-task';
import { Nullable } from './typescript-utils';
import {
    MarketQuotesConfig,
    MarketingSummaryConfig,
    ProposalArtifactConfig,
    ProposalArtifactType,
    QuoteComparisonConfig,
} from '../types/submission-placement-task-config';
import { SubmissionPlacementTaskInfo } from '../types/submission-placement-task';

const DEFAULT_PREMIUM_INCREASE_THRESHOLD = 5; // 5%

export interface SubmissionWithPlacementTasks {
    placementTasks?: SubmissionPlacementTaskInfo[];
}

export function getPlacementTask(submission: Nullable<SubmissionWithPlacementTasks>, taskType: PlacementTask) {
    return submission?.placementTasks?.find((task) => task.placementTask === taskType);
}

export function getProposalArtifacts(submission: SubmissionWithPlacementTasks): ProposalArtifactConfig[] | undefined {
    const sendProposalTask = getPlacementTask(submission, PlacementTask.SendProposal);
    if (!sendProposalTask) {
        return undefined;
    }
    return sendProposalTask?.stepConfig?.proposalArtifacts || [];
}

export function getProposalMarketQuotesConfig(
    submission: SubmissionWithPlacementTasks,
): MarketQuotesConfig | undefined {
    const config = getProposalArtifacts(submission)?.find(
        (artifact) => artifact.type === ProposalArtifactType.MarketQuotes,
    );

    return config ? (config as MarketQuotesConfig) : undefined;
}

export function getPremiumIncreaseThresholdPercent(submission: Nullable<SubmissionWithPlacementTasks>): number {
    const remarketingPlacementTask = getPlacementTask(submission, PlacementTask.Remarketing);
    return remarketingPlacementTask?.stepConfig?.premiumIncreaseThresholdPercent ?? DEFAULT_PREMIUM_INCREASE_THRESHOLD;
}

export function getProposalQuoteComparisonConfig(
    submission: SubmissionWithPlacementTasks,
): QuoteComparisonConfig | undefined {
    const config = getProposalArtifacts(submission)?.find(
        (artifact) => artifact.type === ProposalArtifactType.QuoteComparison,
    );

    return config ? (config as QuoteComparisonConfig) : undefined;
}

export function getProposalMarketingSummaryConfig(
    submission: SubmissionWithPlacementTasks,
): MarketingSummaryConfig | undefined {
    const config = getProposalArtifacts(submission)?.find(
        (artifact) => artifact.type === ProposalArtifactType.MarketingSummary,
    );

    return config ? (config as MarketingSummaryConfig) : undefined;
}
